import {
  EActionCategory,
  EDatasourceType,
  EProjectType,
  type IActionDefinitionDto,
  type IActionParameter
} from "@kuika/kuika-cl-model";
import {
  type IOptionalParameterMenuContext,
  isActionParameterShownInPanel,
  isOptionalActionParameterOffered,
  isOptionalActionParameterSelectableInMenu
} from "./action-parameter-visibility";

/**
 * Which editor the actions panel draws for an action's parameters, and which of the action's
 * parameters that editor lets the user set. One registry, read by:
 * - `action-panel.tsx` `getParameterList` — `resolveActionPanelSurface` picks the branch;
 * - the panel's footer toggles and the optional-parameters menu (`isFooterToggleParameter`,
 *   `isOptionalParameterMenuOffered`);
 * - the action picker's preview — `getUserFacingActionParameters` lists exactly what that editor shows.
 *
 * A symbols group that singles a parameter out (a picker, a switch, a read-only line, nothing at all)
 * is described here by name; `action-parameter-surface.test.ts` fails when `getParameterList` renders
 * a group this registry does not describe.
 */

/** The editors `getParameterList` chooses between. `generic` is its fallback parameter list. */
export enum EActionPanelSurface {
  generic = "generic",
  workflowStart = "workflowStart",
  workflowExternalStart = "workflowExternalStart",
  workflowResume = "workflowResume",
  workflowGenericTasks = "workflowGenericTasks",
  workflowGenericProcesses = "workflowGenericProcesses",
  workflowMigrateProcess = "workflowMigrateProcess",
  navigation = "navigation",
  setValue = "setValue",
  componentToPdf = "componentToPdf",
  setVisible = "setVisible",
  setEnabled = "setEnabled",
  toggleEnableVisible = "toggleEnableVisible",
  toggleMenuCollapse = "toggleMenuCollapse",
  collapsePanel = "collapsePanel",
  selectElementId = "selectElementId",
  photo = "photo",
  ifThenElse = "ifThenElse",
  stripePaymentIntent = "stripePaymentIntent",
  stripeSetupIntent = "stripeSetupIntent",
  notify = "notify",
  updateUserStartingScreen = "updateUserStartingScreen",
  stripeChargeSetupIntent = "stripeChargeSetupIntent",
  saveSource = "saveSource",
  saveActionResult = "saveActionResult",
  search = "search",
  documentParameters = "documentParameters",
  sendMail = "sendMail",
  sendPushNotification = "sendPushNotification",
  printReport = "printReport",
  multiReportPrint = "multiReportPrint",
  triggerEvent = "triggerEvent",
  openInBrowser = "openInBrowser",
  tableConfig = "tableConfig",
  nodeTreeExport = "nodeTreeExport",
  openPdf = "openPdf",
  managedDbSave = "managedDbSave",
  managedDbDelete = "managedDbDelete",
  recurringActions = "recurringActions",
  mqttSubscribe = "mqttSubscribe",
  importMappedExcel = "importMappedExcel",
  confirmation = "confirmation",
  sendWhatsappBusinessMessage = "sendWhatsappBusinessMessage",
  getEntityValueFromDynamicForm = "getEntityValueFromDynamicForm",
  clearDynamicForm = "clearDynamicForm",
  setEntityAttributeValueInDynamicForm = "setEntityAttributeValueInDynamicForm",
  getEntityAttributeValueInDynamicForm = "getEntityAttributeValueInDynamicForm",
  getIsDynamicFormValid = "getIsDynamicFormValid",
  getFromBag = "getFromBag",
  removeFromBag = "removeFromBag",
  bagHasKey = "bagHasKey",
  addToBag = "addToBag",
  fetchFromCache = "fetchFromCache",
  pushToCache = "pushToCache",
  cacheHasKey = "cacheHasKey",
  toggleLayout = "toggleLayout",
  removeFromCache = "removeFromCache"
}

/**
 * How the editor treats a parameter it singles out.
 * - `always`: it draws a control for the parameter whenever the action has it (some only after another
 *   choice, e.g. Navigate's "Disable Smart Execute Page Init" behind its page-init switch).
 * - `rule`: it draws the control under the panel's shared rule — listed when required or switched on,
 *   otherwise addable from the optional-parameters menu.
 * - `switchedOn`: it draws the control only once the parameter is switched on from the
 *   optional-parameters menu (`isExternalVisible === true`) — neither being required nor having a value
 *   draws it. Offered from the menu otherwise, when the menu offers it.
 * - `never`: the user does not set it here (platform-filled, derived, or shown read-only).
 */
export type ActionParameterControlVisibility = "always" | "rule" | "switchedOn" | "never";

/** What an editor does with the parameters it does not single out. */
export type ActionPanelRemainingParameters =
  /** Nothing — only the singled-out parameters have controls. */
  | "none"
  /** One row each, under the shared rule (`isActionParameterShownInPanel` + the optional-parameters menu). */
  | "rule"
  /** One row each, unfiltered. */
  | "all";

export interface IActionParameterControl {
  name: string;
  visibility: ActionParameterControlVisibility;
  /** The label the editor draws for it, when not the parameter's own name. */
  label?: string;
  /** Drawn only on these project types; unknown project type counts as none of them. */
  projectTypes?: readonly EProjectType[];
  /** Drawn only for these of the surface's actions. */
  actionNames?: readonly string[];
}

export interface IActionPanelSurfaceEntry {
  surface: EActionPanelSurface;
  /** The symbols group component `getParameterList` renders for it; `undefined` for the generic list. */
  groupComponent: string | undefined;
  /** Action names routed here. */
  actionNames: readonly string[];
  /** A further condition on the definition beyond its name. */
  matches?: (definition: IActionDefinitionDto) => boolean;
  remaining: ActionPanelRemainingParameters;
  controls: readonly IActionParameterControl[];
}

const control = (
  name: string,
  label?: string,
  visibility: ActionParameterControlVisibility = "always"
): IActionParameterControl => ({ name, label, visibility });

const hidden = (name: string): IActionParameterControl => ({ name, visibility: "never" });

const desktopOnly = (name: string, label: string): IActionParameterControl => ({
  name,
  label,
  visibility: "always",
  projectTypes: [EProjectType.Desktop]
});

/** Parameters `ImportMappedExcelSymbolsGroup` owns; every other parameter gets a generic row from the panel. */
export const IMPORT_MAPPED_EXCEL_STANDARD_PARAMETERS: readonly string[] = [
  "dsId",
  "tableName",
  "primaryKey",
  "mappedColumns",
  "parameters"
];

/** Actions whose panel offers no optional-parameters menu. */
const ACTIONS_WITHOUT_OPTIONAL_PARAMETER_MENU: ReadonlySet<string> = new Set<string>([
  "Notify",
  "ImportMappedExcel",
  "PhotoTake",
  "PhotoFromGallery",
  "PhotosFromGallery"
]);

const DYNAMIC_FORM_CONTROL: IActionParameterControl = control("diId", "Dynamic Form Component");
const BAG_KEY_CONTROL: IActionParameterControl = control("key", "Bag Key");
const CACHE_KEY_CONTROL: IActionParameterControl = control("key", "Cache Key");

/**
 * Every symbols group `getParameterList` routes to, in the order it tests them. Names are disjoint, so
 * order only matters for the entries with a `matches` condition.
 */
export const ACTION_PANEL_SURFACES: readonly IActionPanelSurfaceEntry[] = [
  {
    surface: EActionPanelSurface.workflowStart,
    groupComponent: "WFStartSymbolsGroup",
    actionNames: ["StartProcess"],
    remaining: "rule",
    controls: [control("workflow", "Process"), control("projectId", "External Project"), hidden("parameters")]
  },
  {
    surface: EActionPanelSurface.workflowExternalStart,
    groupComponent: "WFExternalStartSymbolsGroup",
    actionNames: ["StartExternalProcess"],
    remaining: "all",
    controls: [control("projectId", "External Project"), control("workflow", "Process"), hidden("parameters")]
  },
  {
    surface: EActionPanelSurface.workflowResume,
    groupComponent: "WFResumeSymbolsGroup",
    actionNames: ["ResumeProcess", "SaveStepData"],
    remaining: "rule",
    controls: [
      hidden("workflowStepInstanceId"),
      control("userInteractionId", "User Interaction"),
      hidden("isCreatedByWizard")
    ]
  },
  {
    surface: EActionPanelSurface.workflowGenericTasks,
    groupComponent: "WFGenericTasksSymbolsGroup",
    actionNames: ["GetMyTasksFromProcess", "GetUserTasks"],
    remaining: "rule",
    controls: [control("ProcessDefinitionId", "Process"), control("ProcessParameters", "Process Parameters")]
  },
  {
    surface: EActionPanelSurface.workflowGenericProcesses,
    groupComponent: "WFGenericProcessesSymbolsGroup",
    actionNames: ["GetProcessesStartedByUser"],
    remaining: "rule",
    controls: [control("ProcessDefinitionId", "Process")]
  },
  {
    surface: EActionPanelSurface.workflowMigrateProcess,
    groupComponent: "WFMigrateProcessSymbolsGroup",
    actionNames: ["MigrateProcessInstances", "AnalyzeProcessMigration"],
    remaining: "none",
    controls: [control("ProcessDefinitionId", "Process")]
  },
  {
    surface: EActionPanelSurface.navigation,
    groupComponent: "NavigationSymbolsGroup",
    actionNames: ["NAVIGATE", "NavigateToTask"],
    remaining: "none",
    // fromScreenId is set to the current screen; toUrl, title, placemet, closable, width and height
    // have no control here.
    controls: [
      { ...control("WorkflowStepInstanceId", "Step Key"), actionNames: ["NavigateToTask"] },
      { ...control("toScreenId", "To Screen"), actionNames: ["NAVIGATE"] },
      control("openAs", "Open As"),
      control("executePageInitActionsAfterClose", "Execute Page Init Actions After Closed"),
      control("disableSmartExecutePageInit", "Disable Smart Execute Page Init"),
      // An embedded property form for the target navigation component, not a parameter row.
      hidden("navigationKuikaId")
    ]
  },
  {
    surface: EActionPanelSurface.setValue,
    groupComponent: "SetValueSymbolsGroup",
    actionNames: ["SetValueOf"],
    remaining: "none",
    controls: [
      control("targetDiName", "Component to Change"),
      control("value"),
      control("multipleValuePropName", "Prop name to set"),
      control("triggerEvents", "Trigger Events", "rule"),
      control("eventType", "Event Type")
    ]
  },
  {
    surface: EActionPanelSurface.componentToPdf,
    groupComponent: "ComponentToPdfSymbolGroup",
    actionNames: ["ComponentToPdf", "ComponentToPdf2"],
    remaining: "none",
    controls: [
      control("filename"),
      control("targetDiName", "Component to Save as Pdf"),
      control("quality", "Quality"),
      control("orientation", "Orientation", "rule"),
      control("format", "Format")
    ]
  },
  {
    surface: EActionPanelSurface.setVisible,
    groupComponent: "SetVisibleSymbolsGroup",
    actionNames: ["ChangeVisibilityOf"],
    remaining: "none",
    controls: [control("componentName", "Component to Change"), control("visibilityType", "Visibility")]
  },
  {
    surface: EActionPanelSurface.setEnabled,
    groupComponent: "SetEnabledSymbolsGroup",
    actionNames: ["ChangeEnabledOf"],
    remaining: "none",
    controls: [control("componentName", "Component to Change"), control("editabilityType", "Editability")]
  },
  {
    surface: EActionPanelSurface.toggleEnableVisible,
    groupComponent: "SetToogleEnableVisibleSymbolsGroup",
    actionNames: ["ToggleVisibilityOf", "ToggleEditabilityOf"],
    remaining: "none",
    controls: [control("componentName", "Component to Change")]
  },
  {
    // The same group with its `menu` prop: the component list holds only menus.
    surface: EActionPanelSurface.toggleMenuCollapse,
    groupComponent: "SetToogleEnableVisibleSymbolsGroup",
    actionNames: ["ToggleMenuCollapse", "ToggleHamburgerMenuCollapse"],
    remaining: "none",
    controls: [control("componentName", "Component to Change")]
  },
  {
    surface: EActionPanelSurface.collapsePanel,
    groupComponent: "SetCollapsePanelSymbolsGroup",
    actionNames: ["OpenCollapsePanelOf", "CloseCollapsePanelOf", "ToggleCollapsePanelOf"],
    remaining: "none",
    controls: [control("componentName", "Collapse to Change"), control("panelKey", "Panel")]
  },
  {
    surface: EActionPanelSurface.selectElementId,
    groupComponent: "SelectElementIdSymbolGroup",
    actionNames: ["ScrollTo", "SetFocusTo"],
    remaining: "none",
    controls: [control("element", "Select Component")]
  },
  {
    surface: EActionPanelSurface.photo,
    groupComponent: "PhotoTakeymbolsGroup",
    actionNames: ["PhotoTake", "PhotoFromGallery", "PhotosFromGallery"],
    remaining: "none",
    // "Edit Photo" is also withheld from the gallery actions on Desktop projects.
    controls: [control("editPhoto", "Edit Photo"), control("quality", "Photo Quality")]
  },
  {
    surface: EActionPanelSurface.ifThenElse,
    groupComponent: "IfThenElseConditionSymbolGroup",
    actionNames: ["IfThenElse"],
    remaining: "none",
    controls: []
  },
  {
    surface: EActionPanelSurface.stripePaymentIntent,
    groupComponent: "StripePaymentIntentSymbolsGroup",
    actionNames: ["OpenStripePaymentDrawer"],
    remaining: "none",
    controls: [
      control("capturemethod", "Capture Method"),
      control("paymenttype", "Single / Multi Part"),
      control("currency", "Currency"),
      control("supportedPaymentTypes", "Payment Types"),
      control("amount", "Amount (in cents)"),
      control("description", "Description"),
      control("feeamount", "ApplicationFeeAmount"),
      control("destination", "Destination")
    ]
  },
  {
    surface: EActionPanelSurface.stripeSetupIntent,
    groupComponent: "StripeSetupIntentSymbolsGroup",
    actionNames: ["SetupIntent"],
    remaining: "none",
    controls: [control("supportedPaymentTypes", "Payment Types")]
  },
  {
    surface: EActionPanelSurface.notify,
    groupComponent: "NotifySymbolsGroup",
    actionNames: ["Notify"],
    remaining: "none",
    controls: [
      control("message", "Message"),
      control("position", "Position"),
      control("duration", "Duration"),
      control("type", "Type"),
      // Drawn once "Type" is Custom.
      control("backgroundColor", "Background Color"),
      control("textColor", "Text Color"),
      control("fontSize", "Font Size")
    ]
  },
  {
    surface: EActionPanelSurface.updateUserStartingScreen,
    groupComponent: "UpdateUserStartingScreenSymbolsGroup",
    actionNames: ["UpdateUserStartingScreen"],
    remaining: "none",
    controls: [control("userName", "Username"), control("startingScreenId", "Starting Screen")]
  },
  {
    surface: EActionPanelSurface.stripeChargeSetupIntent,
    groupComponent: "StripeChargeSetupIntentSymbolsGroup",
    actionNames: ["ChargePaymentMethod"],
    remaining: "none",
    controls: [
      control("paymentmethod", "Payment Method"),
      control("currency", "Currency"),
      control("amount", "Amount (in cents)"),
      control("description", "Description"),
      control("customerStripeId", "Customer Mail Address"),
      control("paymenttype", "Single / Multi Part"),
      control("feeamount", "ApplicationFeeAmount"),
      control("destination", "Destination")
    ]
  },
  {
    // `param1`…`param5` are relabelled by the chosen source type; their raw names stand in here.
    surface: EActionPanelSurface.saveSource,
    groupComponent: "SaveSourceSymbolsGroup",
    actionNames: ["SaveSource"],
    remaining: "rule",
    controls: []
  },
  {
    surface: EActionPanelSurface.saveActionResult,
    groupComponent: "SaveActionResultSymbolsGroup",
    actionNames: ["SaveActionSource"],
    remaining: "none",
    // actionName, jQSchema, actionResult and name are derived from the picked action on save.
    controls: [
      control("actionId", "Select Action"),
      control("deleteOlderRecords", "Update Strategy"),
      control("primaryKeys", "Primary Keys"),
      control("metadataJQSchema", "Metadata Fields", "rule"),
      control("collectionName", undefined, "rule")
    ]
  },
  {
    // `sessionId` is offered once "RAG Search" is the search type, and `topResult` is withdrawn then.
    surface: EActionPanelSurface.search,
    groupComponent: "SearchSymbolsGroup",
    actionNames: ["Search"],
    remaining: "rule",
    controls: []
  },
  {
    // Rows come from the project's declared document parameters, not from the definition.
    surface: EActionPanelSurface.documentParameters,
    groupComponent: "DocumentParametersSymbolsGroup",
    actionNames: ["ResolveDocumentParameters"],
    remaining: "none",
    controls: []
  },
  {
    // `templateId` is set by the "Mail Type" / "Templates" pickers, never as a parameter row.
    surface: EActionPanelSurface.sendMail,
    groupComponent: "SendMailWithTemplateSymbolsGroup",
    actionNames: ["SendMail", "SendMailWithTemplate"],
    remaining: "rule",
    controls: [hidden("templateId"), control("fileAttachments", "Attachments", "rule")]
  },
  {
    surface: EActionPanelSurface.sendPushNotification,
    groupComponent: "SendPushNotificationSymbolsGroup",
    actionNames: ["SendPushNotification", "GenerateLink"],
    remaining: "rule",
    controls: [
      control("url", "To Url", "rule"),
      control("deviceType", "Device Type"),
      control("appId", "To Project", "rule"),
      control("toScreenId", "To Screen", "rule")
    ]
  },
  {
    surface: EActionPanelSurface.printReport,
    groupComponent: "PrintReportSymbolsGroup",
    actionNames: ["PrintReport"],
    remaining: "none",
    controls: [control("reportName", "Select Report")]
  },
  {
    surface: EActionPanelSurface.multiReportPrint,
    groupComponent: "MultiReportPrintSymbolsGroup",
    actionNames: ["GetMultipleReportsBase64"],
    remaining: "none",
    controls: []
  },
  {
    surface: EActionPanelSurface.triggerEvent,
    groupComponent: "TriggerEventSymbolsGroup",
    actionNames: ["TriggerEvent"],
    remaining: "none",
    // The event name is shown read-only; it follows from the picked component's event.
    controls: [control("targetDiId", "Select DI"), hidden("targetEventName")]
  },
  {
    surface: EActionPanelSurface.openInBrowser,
    groupComponent: "OpenInBrowserSymbolsGroup",
    actionNames: ["OpenInBrowser"],
    remaining: "rule",
    // The group draws its "Open As" picker only when `openAs.isExternalVisible === true`, and never as a row.
    controls: [control("openAs", "Open As", "switchedOn")]
  },
  {
    surface: EActionPanelSurface.tableConfig,
    groupComponent: "TableConfigSymbolsGroup",
    actionNames: [
      "ExportTableConfigs",
      "ImportTableConfigs",
      "ClearTableFilteringConfigs",
      "ResetTableConfigs",
      "SearchTable",
      "ExportTable"
    ],
    remaining: "rule",
    controls: [control("tableName", "Select a Table")]
  },
  {
    surface: EActionPanelSurface.nodeTreeExport,
    groupComponent: "NodeTreeExportSymbolsGroup",
    actionNames: ["ExportNodeTree"],
    remaining: "none",
    controls: [control("nodeTreeName", "Select a Node Tree")]
  },
  {
    // `warnLandscape` has no control in this group.
    surface: EActionPanelSurface.openPdf,
    groupComponent: "OpenPdfSymbolsGroup",
    actionNames: ["OpenPdf"],
    remaining: "none",
    controls: [control("PdfUrl"), control("openAs", "Open As")]
  },
  {
    // Edits `managedDbData` (table, columns, conditions); the definition carries no parameters.
    surface: EActionPanelSurface.managedDbSave,
    groupComponent: "ManagedDbSaveSymbolGroup",
    actionNames: ["SaveRecord"],
    matches: (definition: IActionDefinitionDto): boolean => definition.actionCategory === EActionCategory.ManagedDb,
    remaining: "none",
    controls: []
  },
  {
    surface: EActionPanelSurface.managedDbDelete,
    groupComponent: "ManagedDbDeleteSymbolGroup",
    actionNames: ["DeleteRecord"],
    matches: (definition: IActionDefinitionDto): boolean => definition.actionCategory === EActionCategory.ManagedDb,
    remaining: "none",
    controls: []
  },
  {
    surface: EActionPanelSurface.recurringActions,
    groupComponent: "RecurringActionsSymbolGroup",
    actionNames: ["RecurringActions"],
    remaining: "all",
    controls: [control("recurringInterval", "Interval (seconds)")]
  },
  {
    surface: EActionPanelSurface.mqttSubscribe,
    groupComponent: "MqttSubscribeActionsSymbolGroup",
    actionNames: [],
    matches: (definition: IActionDefinitionDto): boolean =>
      definition.actionCategory === EActionCategory.Custom &&
      definition.actionType === EDatasourceType.Mqtt &&
      definition.name.endsWith("_Subscribe"),
    remaining: "all",
    controls: []
  },
  {
    // The group shows the table, key and mapped columns read-only; the panel adds a row for the rest.
    surface: EActionPanelSurface.importMappedExcel,
    groupComponent: "ImportMappedExcelSymbolsGroup",
    actionNames: ["ImportMappedExcel"],
    remaining: "all",
    controls: IMPORT_MAPPED_EXCEL_STANDARD_PARAMETERS.map(hidden)
  },
  {
    surface: EActionPanelSurface.confirmation,
    groupComponent: "ConfirmationSymbolGroup",
    actionNames: ["Confirmation"],
    remaining: "none",
    controls: [
      control("confirmationMessage", "Confirmation Message"),
      desktopOnly("confirmationFontFamily", "Font Family"),
      desktopOnly("confirmationHeader", "Confirmation Header"),
      desktopOnly("confirmationIcon", "Confirmation Icon"),
      desktopOnly("confirmationIconBackgroundColor", "Icon Background Color"),
      desktopOnly("confirmationConfirmButtonColor", "Confirm Button Color"),
      desktopOnly("confirmationCancelButtonColor", "Cancel Button Color")
    ]
  },
  {
    // Template variables and button mappings are drawn from the picked WhatsApp template.
    surface: EActionPanelSurface.sendWhatsappBusinessMessage,
    groupComponent: "SendWhatsappBusinessMessageSymbolsGroup",
    actionNames: ["SendWhatsappBusinessMessage"],
    remaining: "none",
    controls: [
      control("phoneNumber"),
      control("templateName", "Template Name"),
      hidden("buttons"),
      hidden("allInteractionLinks")
    ]
  },
  {
    surface: EActionPanelSurface.getEntityValueFromDynamicForm,
    groupComponent: "GetEntityValueFromDynamicFormSymbolsGroup",
    actionNames: ["GetEntityValueFromDynamicForm"],
    remaining: "none",
    controls: [DYNAMIC_FORM_CONTROL]
  },
  {
    surface: EActionPanelSurface.clearDynamicForm,
    groupComponent: "ClearDynamicFormSymbolsGroup",
    actionNames: ["ClearDynamicForm"],
    remaining: "none",
    controls: [DYNAMIC_FORM_CONTROL]
  },
  {
    surface: EActionPanelSurface.setEntityAttributeValueInDynamicForm,
    groupComponent: "SetEntityAttributeValueInDynamicFormSymbolsGroup",
    actionNames: ["SetEntityAttributeValueInDynamicForm"],
    remaining: "none",
    controls: [DYNAMIC_FORM_CONTROL, control("attributeId"), control("value")]
  },
  {
    surface: EActionPanelSurface.getEntityAttributeValueInDynamicForm,
    groupComponent: "GetEntityAttributeValueInDynamicFormSymbolsGroup",
    actionNames: ["GetEntityAttributeValueInDynamicForm"],
    remaining: "none",
    controls: [DYNAMIC_FORM_CONTROL, control("attributeId")]
  },
  {
    surface: EActionPanelSurface.getIsDynamicFormValid,
    groupComponent: "GetIsDynamicFormValidSymbolsGroup",
    actionNames: ["GetIsDynamicFormValid"],
    remaining: "none",
    controls: [DYNAMIC_FORM_CONTROL]
  },
  {
    surface: EActionPanelSurface.getFromBag,
    groupComponent: "GetFromBagSymbolsGroup",
    actionNames: ["GetFromBag"],
    remaining: "none",
    controls: [BAG_KEY_CONTROL]
  },
  {
    surface: EActionPanelSurface.removeFromBag,
    groupComponent: "RemoveFromBagSymbolsGroup",
    actionNames: ["RemoveFromBag"],
    remaining: "none",
    controls: [BAG_KEY_CONTROL]
  },
  {
    surface: EActionPanelSurface.bagHasKey,
    groupComponent: "BagHasKeySymbolsGroup",
    actionNames: ["BagHasKey"],
    remaining: "none",
    controls: [BAG_KEY_CONTROL]
  },
  {
    surface: EActionPanelSurface.addToBag,
    groupComponent: "AddToBagSymbolsGroup",
    actionNames: ["AddToBag"],
    remaining: "none",
    controls: [BAG_KEY_CONTROL, control("bagItem", "Bag Item")]
  },
  {
    surface: EActionPanelSurface.fetchFromCache,
    groupComponent: "FetchFromCacheSymbolsGroup",
    actionNames: ["FetchFromCache"],
    remaining: "none",
    controls: [CACHE_KEY_CONTROL]
  },
  {
    surface: EActionPanelSurface.pushToCache,
    groupComponent: "PushToCacheSymbolsGroup",
    actionNames: ["PushToCache"],
    remaining: "none",
    controls: [CACHE_KEY_CONTROL, control("bagItem", "Cache Item")]
  },
  {
    surface: EActionPanelSurface.cacheHasKey,
    groupComponent: "CacheHasKeySymbolsGroup",
    actionNames: ["CheckKeyExistence"],
    remaining: "none",
    controls: [CACHE_KEY_CONTROL]
  },
  {
    surface: EActionPanelSurface.toggleLayout,
    groupComponent: "ToggleLayoutSymbolsGroup",
    actionNames: ["ToggleLayout"],
    remaining: "none",
    controls: [control("position", "Layout Position")]
  },
  {
    surface: EActionPanelSurface.removeFromCache,
    groupComponent: "RemoveFromCacheSymbolsGroup",
    actionNames: ["RemoveFromCache"],
    remaining: "none",
    controls: [CACHE_KEY_CONTROL]
  }
];

/** The panel's fallback: one row per parameter under the shared rule, minus the footer toggles. */
const GENERIC_SURFACE: IActionPanelSurfaceEntry = {
  surface: EActionPanelSurface.generic,
  groupComponent: undefined,
  actionNames: [],
  remaining: "rule",
  controls: []
};

/** An entry routes by name, by `matches`, or by both (an entry without names routes on `matches` alone). */
const entryMatches = (entry: IActionPanelSurfaceEntry, definition: IActionDefinitionDto): boolean => {
  if (entry.actionNames.length > 0 && !entry.actionNames.includes(definition.name)) {
    return false;
  }
  return entry.matches ? entry.matches(definition) : true;
};

/** The registry entry for the editor `getParameterList` draws for this action. */
export function getActionPanelSurfaceEntry(definition: IActionDefinitionDto): IActionPanelSurfaceEntry {
  return (
    ACTION_PANEL_SURFACES.find((entry: IActionPanelSurfaceEntry): boolean => entryMatches(entry, definition)) ??
    GENERIC_SURFACE
  );
}

/** Which editor `getParameterList` draws for this action. */
export function resolveActionPanelSurface(definition: IActionDefinitionDto): EActionPanelSurface {
  return getActionPanelSurfaceEntry(definition).surface;
}

/**
 * A parameter the panel sets with a switch in its footer rather than a row: a JavaScript action's
 * "Run on Server" and Get Audit Logs' "Show this project's logs".
 */
export function isFooterToggleParameter(definition: IActionDefinitionDto, parameterName: string): boolean {
  if (parameterName === "runOnServer") {
    return definition.actionType === EDatasourceType.JavaScriptAction;
  }
  return parameterName === "onlyCurrentProject" && definition.name === "GetAuditLogs";
}

/** Whether the panel offers the optional-parameters menu for this action (`action.label` is its name). */
export function isOptionalParameterMenuOffered(actionName: string): boolean {
  return !ACTIONS_WITHOUT_OPTIONAL_PARAMETER_MENU.has(actionName);
}

export interface IUserFacingActionParameter {
  parameter: IActionParameter;
  /** The label the panel draws for it. */
  label: string;
  /** Not listed on a fresh action; the user adds it from the optional-parameters menu. */
  isAddable: boolean;
}

const findControl = (entry: IActionPanelSurfaceEntry, parameterName: string): IActionParameterControl | undefined =>
  entry.controls.find((candidate: IActionParameterControl): boolean => candidate.name === parameterName);

/** `true` when the optional-parameters menu offers it, `undefined` when it does not. */
const offeredFromMenu = (
  parameter: IActionParameter,
  menuOffered: boolean,
  context: IOptionalParameterMenuContext
): true | undefined => {
  const isOffered: boolean =
    menuOffered &&
    isOptionalActionParameterOffered(parameter) &&
    isOptionalActionParameterSelectableInMenu(parameter, context);
  return isOffered ? true : undefined;
};

/** `undefined` when the panel would neither list nor offer it. */
const underSharedRule = (
  parameter: IActionParameter,
  menuOffered: boolean,
  context: IOptionalParameterMenuContext
): boolean | undefined => {
  if (isActionParameterShownInPanel(parameter)) {
    return false;
  }
  return offeredFromMenu(parameter, menuOffered, context);
};

/** `undefined` when the group would neither draw it nor the menu offer it. */
const underSwitchedOnRule = (
  parameter: IActionParameter,
  menuOffered: boolean,
  context: IOptionalParameterMenuContext
): boolean | undefined => {
  if (parameter.isExternalVisible === true) {
    return false;
  }
  return offeredFromMenu(parameter, menuOffered, context);
};

/**
 * The action's parameters exactly as its actions-panel editor presents them on a freshly added action:
 * the rows and controls the user sets, plus the optionals they can add from the menu (`isAddable`).
 * Parameters the platform fills, a group derives, or a footer switch owns stay out.
 */
export function getUserFacingActionParameters(
  definition: IActionDefinitionDto,
  context: IOptionalParameterMenuContext = {}
): IUserFacingActionParameter[] {
  const entry: IActionPanelSurfaceEntry = getActionPanelSurfaceEntry(definition);
  const menuOffered: boolean = isOptionalParameterMenuOffered(definition.name);
  const result: IUserFacingActionParameter[] = [];

  (definition.actionParameters ?? []).forEach((parameter: IActionParameter): void => {
    const ownLabel: string = parameter.displayName || parameter.name;
    const singledOut: IActionParameterControl | undefined = findControl(entry, parameter.name);
    let isAddable: boolean | undefined;
    let label: string = ownLabel;

    if (singledOut) {
      label = singledOut.label ?? ownLabel;
      const onThisProject: boolean =
        !singledOut.projectTypes ||
        (context.projectType !== undefined && singledOut.projectTypes.includes(context.projectType));
      const forThisAction: boolean = !singledOut.actionNames || singledOut.actionNames.includes(definition.name);
      if (singledOut.visibility === "never" || !onThisProject || !forThisAction) {
        isAddable = undefined;
      } else if (singledOut.visibility === "always") {
        isAddable = false;
      } else if (singledOut.visibility === "switchedOn") {
        isAddable = underSwitchedOnRule(parameter, menuOffered, context);
      } else {
        isAddable = underSharedRule(parameter, menuOffered, context);
      }
    } else if (entry.surface === EActionPanelSurface.generic && isFooterToggleParameter(definition, parameter.name)) {
      isAddable = undefined;
    } else if (entry.remaining === "all") {
      isAddable = false;
    } else if (entry.remaining === "rule") {
      isAddable = underSharedRule(parameter, menuOffered, context);
    }

    if (isAddable !== undefined) {
      result.push({ parameter, label, isAddable });
    }
  });
  return result;
}
